import { useState, type FormEvent } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';

const FORM_ADDR = 'https://readdy.ai/api/form/db7n45fidngsrf4ehnbg';

const contactInfo = [
  { icon: 'ri-mail-line', label: '邮箱', value: 'hello@caominmovie.com' },
  { icon: 'ri-phone-line', label: '商务合作', value: '400-800-2025' },
  { icon: 'ri-map-pin-line', label: '办公地址', value: '上海市黄浦区影像创意园 3 号楼 502' },
  { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 10:00 - 19:00' },
];

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  usePageMeta({
    title: '联系我们 - 草民电影',
    description:
      '欢迎向草民电影反馈建议、提供线索或洽谈合作。你可以通过邮箱、电话联系我们，也可以直接提交留言表单。',
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') ?? '').trim();
    if (honeypot) {
      formData.delete('contact_alt');
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('contact_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, value as string);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';

      const isSpam = serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="w-full">
      <section className="w-full bg-primary-800 px-4 py-12 md:px-6 md:py-16">
        <div className="[&_a]:text-background-300 [&_span]:text-background-300">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '联系我们' }]} />
        </div>
        <h1 className="mt-5 font-heading text-3xl font-bold text-background-50 md:text-4xl">联系我们</h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">
          无论是内容建议、线索提供还是商务合作，都欢迎与我们联系。
        </p>
      </section>

      <section className="w-full px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系方式</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              我们会认真阅读每一条留言，并尽量在三个工作日内回复。
            </p>
            <div className="mt-6 flex flex-col gap-4">
              {contactInfo.map((info) => (
                <div
                  key={info.label}
                  className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xl text-primary-700">
                    <i className={info.icon} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400">{info.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{info.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">给我们留言</h2>
            <p className="mt-2 text-sm text-foreground-500">带 <span className="text-accent-600">*</span> 的为必填项。</p>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
              <div>
                <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  姓名 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的称呼"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  邮箱 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  留言内容 <span className="text-accent-600">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="想对我们说的话（最多 500 字）"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>

              <input
                className="alt-contact-field"
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === 'success' && (
                <p className="flex items-center gap-2 rounded-md bg-accent-50 px-4 py-3 text-sm text-accent-800">
                  <i className="ri-checkbox-circle-line" /> 留言已提交，感谢你的反馈，我们会尽快回复。
                </p>
              )}
              {status === 'error' && (
                <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                  <i className="ri-error-warning-line" /> {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> 提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" /> 提交留言
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}